import { AppLink, Button, Field, Input } from "@nodus/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import * as React from "react";
import { AuthCard, safeRedirect } from "../../../app/auth-card";
import { useCaptcha } from "../../../app/turnstile";
import { useAuth } from "../../../auth/auth-context";

export const Route = createFileRoute("/verify-email")({
  validateSearch: (s: Record<string, unknown>): { email?: string; redirect?: string } => ({
    ...(typeof s.email === "string" ? { email: s.email } : {}),
    ...(typeof s.redirect === "string" ? { redirect: s.redirect } : {}),
  }),
  component: VerifyEmail,
});

function VerifyEmail() {
  const { email, redirect } = Route.useSearch();
  return email ? <EnterCode email={email} redirect={redirect} /> : <AskEmail redirect={redirect} />;
}

/** Reached without a sign-up in this tab (a new tab or a bookmark): ask which address the code went to. */
function AskEmail({ redirect }: { redirect?: string }) {
  const navigate = useNavigate();
  const [email, setEmail] = React.useState("");
  return (
    <AuthCard
      title="Verify your email"
      description="Enter the email you signed up with, then the 6-digit code we sent to it."
      footer={
        <>
          No account yet?{" "}
          <AppLink href="/sign-up" className="font-medium text-fg-primary underline">
            Sign up
          </AppLink>
        </>
      }
    >
      <form
        className="grid gap-4"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void navigate({ to: "/verify-email", search: { email: email.trim(), ...(redirect ? { redirect } : {}) } });
        }}
      >
        <Field id="email" label="Email" required>
          <Input type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Button type="submit" disabled={!email.trim().includes("@")}>Continue</Button>
      </form>
    </AuthCard>
  );
}

function EnterCode({ email, redirect }: { email: string; redirect?: string }) {
  const { auth } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = React.useState("");
  const [error, setError] = React.useState<string>();
  const [sent, setSent] = React.useState(false);
  const [resending, setResending] = React.useState(false);
  // Only the resend reaches a captcha-checked endpoint; verifying the code does not.
  const captcha = useCaptcha();

  async function resend() {
    setResending(true);
    setError(undefined);
    try {
      await auth.resendVerification(email, captcha.token);
      setSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not send the code again.");
    } finally {
      setResending(false);
      captcha.reset();
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(undefined);
    try {
      await auth.verifyEmailOtp(email, code.trim());
      void navigate({ to: safeRedirect(redirect) === "/" ? "/onboarding" : safeRedirect(redirect) });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Verification failed.");
    }
  }

  return (
    <AuthCard title="Check your email" description={<>We sent a 6-digit code to <strong className="text-fg-body">{email}</strong>.</>}>
      <form onSubmit={submit} className="grid gap-4" noValidate>
        <Field id="code" label="Verification code" required>
          <Input inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} className="font-mono tracking-[0.3em]" />
        </Field>
        {error ? <p role="alert" className="text-ui text-danger-fg">{error}</p> : null}
        <Button type="submit" disabled={code.length !== 6}>Verify email</Button>
        {captcha.widget}
        <Button variant="link" disabled={resending || !captcha.ready} onClick={() => void resend()}>
          {sent ? "Code sent again" : "Resend code"}
        </Button>
      </form>
    </AuthCard>
  );
}
